import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import ReceiptVoucherForm from '../ReceiptVoucherForm';
import { CreateReceiptVoucherRequest, UpdateReceiptVoucherRequest, ReceiptVoucherResponse } from '@/types/receiptVoucher';
import { receiptVoucherApi } from '@/api/endpoints/receiptVouchers';
import { toast } from '@/store/toastStore';
import { useUIStore } from '@/store/uiStore';
import { Customer } from '@/types/customer';

interface CreateReceiptVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Called with the created voucher - useful for chaining to confirm modal */
  onCreated?: (voucher: ReceiptVoucherResponse) => void;
  initialCustomer?: Customer;
}

const CreateReceiptVoucherModal: React.FC<CreateReceiptVoucherModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onCreated,
  initialCustomer,
}) => {
  const { t } = useTranslation();
  const { refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (data: CreateReceiptVoucherRequest | UpdateReceiptVoucherRequest) => {
    setLoading(true);
    try {
      const createdVoucher = await receiptVoucherApi.createReceiptVoucher(data as CreateReceiptVoucherRequest);
      toast.success(t('receiptVoucher.messages.createSuccess'));
      
      // Trigger refresh in parent component
      refreshCallbacks.receiptVouchers?.();
      
      onSuccess?.();
      onCreated?.(createdVoucher);
      onClose();
    } catch (error: any) {
      console.error('Error creating receipt voucher:', error);
      toast.error(
        error.response?.data?.message || 
        t('receiptVoucher.messages.createError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('receiptVoucher.modals.create.title')}
      size="2xl"
    >
      <div className="p-6">
        <ReceiptVoucherForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleClose}
          loading={loading}
          initialCustomer={initialCustomer}
        />
      </div>
    </Modal>
  );
};

export default CreateReceiptVoucherModal;
